import { useState } from 'react'
import { isAxiosError } from 'axios'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import ConfirmModal from '@/components/common/ConfirmModal'
import { applyFleetBulkOverride } from './api'
import { plural, siteListLabel, type TopicGroup } from './severityRules'

/**
 * Bulk apply, unchanged in behaviour: writes individual (per-alarm)
 * settings for the chosen alarm types at the selected sites through
 * POST /fleet/bulk. One card per severity so every severity is reachable
 * without scrolling through other severities' alarm types.
 */
export default function BulkApplySection({
  topicGroups,
  selectedIds,
  siteNames,
}: {
  topicGroups: TopicGroup[]
  selectedIds: string[]
  siteNames: string[]
}) {
  return (
    <section aria-labelledby="bulk-apply-heading" className="rounded-2xl border border-nav-line bg-card p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="bulk-apply-heading" className="text-base font-semibold text-ink">
          3. Individual alarms (bulk apply)
        </h2>
        <span className="rounded-full bg-brand/10 px-2.5 py-0.5 text-xs font-semibold text-brand">
          Your notifications · selected sites
        </span>
      </div>
      <p className="mt-1 text-sm text-text-secondary">
        Turn specific alarm types on or off at the selected sites. These are individual settings: they take priority
        over severity subscriptions and severity defaults for those alarms, and don’t include alarm types added later.
      </p>
      {selectedIds.length === 0 && (
        <p role="status" className="mt-3 rounded-2xl border border-nav-line bg-bg-tertiary p-3 text-sm text-text-secondary">
          Choose at least one site to apply individual alarm settings.
        </p>
      )}
      <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {topicGroups.map((group) => (
          <BulkApplyCard
            key={group.severityId ?? 'uncategorised'}
            group={group}
            selectedIds={selectedIds}
            siteNames={siteNames}
          />
        ))}
      </div>
    </section>
  )
}

function BulkApplyCard({
  group,
  selectedIds,
  siteNames,
}: {
  group: TopicGroup
  selectedIds: string[]
  siteNames: string[]
}) {
  const queryClient = useQueryClient()
  const [chosenTopics, setChosenTopics] = useState<Set<number>>(() => new Set(group.topics.map((t) => t.alarm_topic_id)))
  const [confirm, setConfirm] = useState<boolean | null>(null)
  const [message, setMessage] = useState<{ text: string; isError: boolean } | null>(null)
  const testId = `bulk-${group.severityId ?? 'uncategorised'}`

  const mutation = useMutation({
    mutationFn: (isSubscribed: boolean) => {
      const overrides: Record<number, boolean> = {}
      for (const topicId of chosenTopics) overrides[topicId] = isSubscribed
      return applyFleetBulkOverride(selectedIds, overrides)
    },
    onSuccess: (result, isSubscribed) => {
      setConfirm(null)
      setMessage({
        isError: false,
        text:
          `Turned ${isSubscribed ? 'on' : 'off'} ${plural(result.topics_updated, 'alarm type')} at ${plural(result.systems_updated, 'site')}.` +
          (result.skipped_controller_ids.length
            ? ` ${plural(result.skipped_controller_ids.length, 'site')} skipped (not accessible to you).`
            : ''),
      })
      queryClient.invalidateQueries({ queryKey: ['fleet-alarm-overview'] })
    },
    onError: (err) => {
      setConfirm(null)
      const apiError = isAxiosError(err) ? (err.response?.data as { error?: string } | undefined)?.error : undefined
      setMessage({ isError: true, text: apiError || 'Failed to apply. Nothing was changed.' })
    },
  })

  function toggle(id: number) {
    setChosenTopics((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const canApply = selectedIds.length > 0 && chosenTopics.size > 0 && !mutation.isPending
  const scope = `${plural(chosenTopics.size, 'alarm type')} at ${plural(selectedIds.length, 'site')}`

  return (
    <div data-testid={testId} className="flex flex-col rounded-2xl border border-nav-line p-3">
      <h3 className="text-sm font-semibold text-ink">{group.name}</h3>
      <p className="text-xs text-text-secondary">
        {group.severityId === null
          ? 'Alarm types with no severity. They can only be set individually.'
          : `${plural(group.topics.length, 'alarm type')}`}
      </p>
      <p className="mt-1 text-xs text-text-primary">
        Applies to:{' '}
        {chosenTopics.size === group.topics.length
          ? group.topics.length === 1
            ? 'its 1 alarm type'
            : `all ${group.topics.length} alarm types`
          : `${chosenTopics.size} of ${plural(group.topics.length, 'alarm type')}`}
        {selectedIds.length > 0 ? ` at ${siteListLabel(siteNames)}` : ''}
      </p>
      <details className="mt-2 text-xs">
        <summary className="cursor-pointer font-semibold text-accent">Review and choose alarms</summary>
        <div className="mt-1 flex gap-3">
          <button
            type="button"
            onClick={() => setChosenTopics(new Set(group.topics.map((t) => t.alarm_topic_id)))}
            className="text-accent hover:underline"
          >
            All
          </button>
          <button type="button" onClick={() => setChosenTopics(new Set())} className="text-accent hover:underline">
            None
          </button>
        </div>
        <div className="mt-1 max-h-40 overflow-y-auto">
          {group.topics.map((t) => (
            <label key={t.alarm_topic_id} className="flex items-center gap-2 py-0.5 text-text-primary">
              <input type="checkbox" checked={chosenTopics.has(t.alarm_topic_id)} onChange={() => toggle(t.alarm_topic_id)} />
              {t.alarm_real_name}
            </label>
          ))}
        </div>
      </details>
      {chosenTopics.size === 0 && <p className="mt-1 text-xs text-error">Choose at least one alarm type.</p>}
      <div className="mt-auto flex flex-wrap gap-2 pt-3">
        <button
          type="button"
          disabled={!canApply}
          onClick={() => setConfirm(true)}
          className="whitespace-nowrap rounded-full bg-brand px-4 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
        >
          Turn on
        </button>
        <button
          type="button"
          disabled={!canApply}
          onClick={() => setConfirm(false)}
          className="whitespace-nowrap rounded-full border border-nav-line px-4 py-1.5 text-xs font-semibold text-text-primary hover:bg-nav-hover disabled:opacity-50"
        >
          Turn off
        </button>
      </div>
      {message && (
        <p role="status" className={`mt-2 text-xs ${message.isError ? 'text-error' : 'text-brand'}`}>
          {message.text}
        </p>
      )}
      <ConfirmModal
        isOpen={confirm !== null}
        title={`Turn ${confirm ? 'on' : 'off'} ${group.name} alarms?`}
        message={`This saves an individual setting (${confirm ? 'on' : 'off'}) for ${scope}: ${siteListLabel(siteNames, 5)}. Individual settings take priority over your severity subscriptions and severity defaults.`}
        confirmLabel={confirm ? 'Turn on' : 'Turn off'}
        isDangerous={confirm === false}
        isConfirming={mutation.isPending}
        onConfirm={() => confirm !== null && mutation.mutate(confirm)}
        onCancel={() => setConfirm(null)}
      />
    </div>
  )
}
